import { useCallback, useState } from 'react';
import type { Command } from '../../menu/types';
import { useDeleteTrackingScreenshotMutation } from '../api/gridApi';
import { whenCommand } from '../commands/utils';
import { extractErrorMessage } from '../../../utils/errorUtils';

type UseDeleteScreenshotArgs = {
  commands?: Command[];
  contentKey: string;
  onRefresh?: () => void;
  requestDeleteConfirmation?: (message: string) => Promise<boolean>;
  onNotify: (message: string, severity: 'success' | 'error') => void;
};

/**
 * Row-scoped "delete screenshot" action, wired directly to the backend
 * command "deleteScreenshot" the same way onDelete/onRestore work: no prop
 * drilling from parents, enabled purely by the command's presence in
 * metadata.commands.
 */
export function useDeleteScreenshot({
  commands,
  contentKey,
  onRefresh,
  requestDeleteConfirmation,
  onNotify,
}: UseDeleteScreenshotArgs) {
  const [deleteScreenshotTrigger] = useDeleteTrackingScreenshotMutation();
  const [deletingScreenshotIds, setDeletingScreenshotIds] = useState<Set<string>>(new Set());

  const handleDeleteScreenshot = useCallback(
    async (row: Record<string, unknown>) => {
      const id = row.Id;
      if (!id) return;

      if (requestDeleteConfirmation) {
        let confirmed: boolean;
        try {
          confirmed = await requestDeleteConfirmation(
            'Are you sure you want to delete this screenshot?'
          );
        } catch (error) {
          onNotify(extractErrorMessage(error, 'Failed to confirm deletion'), 'error');
          return;
        }
        if (!confirmed) return;
      }

      const rowId = String(id);
      setDeletingScreenshotIds((prev) => new Set(prev).add(rowId));
      try {
        await deleteScreenshotTrigger({ id: rowId, nodeKey: contentKey }).unwrap();
        onNotify('Screenshot deleted successfully', 'success');
        onRefresh?.();
      } catch (error) {
        onNotify(extractErrorMessage(error, 'Failed to delete screenshot'), 'error');
      } finally {
        setDeletingScreenshotIds((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
      }
    },
    [deleteScreenshotTrigger, contentKey, onRefresh, requestDeleteConfirmation, onNotify]
  );

  const onDeleteScreenshot = whenCommand(commands, 'deletescreenshot', handleDeleteScreenshot);

  return { onDeleteScreenshot, deletingScreenshotIds };
}
